import { Component, Inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { BranchSelectionService } from '../../../Services/branch-selection.service';
import { LoadingService } from '../../../Services/Loading/loading-service.service';
import { Chart } from 'chart.js';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
/* istanbul ignore next */
@Component({
  selector: 'app-cpcm-details',
  templateUrl: './CMCP-details-dialog.component.html',
  styleUrls: ['./CMCP-details-dialog.component.scss'],

  standalone: false,
})
export class CMCPDetailsDialogComponent {
  private chart: Chart | null = null;
  chartData: { label: string; value: number }[] = [];
  private destroy$ = new Subject<void>();
  branchId: string | null = null;
  implementationName: string | null = null;
  anniversaryDate: string | null = null;
  totalLicenses: number | null = null;
  availableLicenses: number | null = null;
  activeLicenses: number | null = null;
  clientId: string | null = null;
  mappingType: number | null = null;
  activeLicensesData: { label: string; value: number }[] = [];
  totalLicensesData: { label: string; value: number }[] = [];
  /* istanbul ignore next */
  constructor(
    private route: ActivatedRoute,
    public backend: BackendAccessService,
    public datePipe: DatePipe,
    private branchSelectionService: BranchSelectionService,
    public loadingService: LoadingService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.handleResize = this.handleResize.bind(this);
  }
  /* istanbul ignore next */
  ngOnInit() {
    if (this.data) {
      this.implementationName = this.data.implementationName;
      this.branchId = this.data.branchId ?? '';
      this.clientId = this.data.clientId;
      this.mappingType = this.data.mappingType;
    }
    this.loadCMCPDetails();
  }

  /* istanbul ignore next */
  ngAfterViewInit() {
    window.addEventListener('resize', this.handleResize);
  }
  /* istanbul ignore next */
  ngOnDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.destroy$.next();
    this.destroy$.complete();
  }
  /* istanbul ignore next */
  handleResize() {
    if (this.chart) {
      this.chart.resize();
    }
  }
  /* istanbul ignore next */
  loadCMCPDetails() {
    this.loadingService.show();
    if (this.data) {
      this.backend
        .getCPCMDetailsData(this.data.clid, this.data.brid, this.data.mpt)
        .subscribe({
          next: (details) => {
            this.anniversaryDate = details.anniversaryDate;
            this.totalLicenses = details.totalLicenses;
            this.availableLicenses = details.availableLicenses;
            this.activeLicenses = details.activeLicenses;
            this.processData(details);
            this.createChart(this.activeLicensesData, this.totalLicensesData);
            this.loadingService.hide();
          },
          error: (error) => {
            console.error('Failed to load CP/CM details:', error);
            this.loadingService.hide();
          },
        });
    } else {
      console.error('Invalid data received in CMCPDetailsDialogComponent');
      this.loadingService.hide();
    }
  }

  /* istanbul ignore next */
  processData(data: any) {
    this.loadingService.show();
    if (data && data.monthlyData) {
      const activeLicensesData = data.monthlyData.map(
        (item: { createdMonth: any; createdYear: any; activeUsers: any }) => ({
          label: `${item.createdMonth} ${item.createdYear}`,
          value: item.activeUsers,
        })
      );
      const totalLicensesData = data.monthlyData.map(
        (item: { createdMonth: any; createdYear: any; totalUsers: any }) => ({
          label: `${item.createdMonth} ${item.createdYear}`,
          value: item.totalUsers,
        })
      );
      // Assuming you have a way to store these processed data points for chart creation
      this.activeLicensesData = activeLicensesData;
      this.totalLicensesData = totalLicensesData;
      // Similar processing for totalLicensesData if needed
    } else {
      // Handle case where data or monthlyData is not available
      console.warn('Data is not available for chart processing');
    }
    this.loadingService.hide();
  }
  /* istanbul ignore next */
  createChart(activeLicensesData: any, totalLicensesData: any) {
    // Check if the data arrays are null or undefined
    if (!activeLicensesData || !totalLicensesData) {
      console.error('Data not available for creating chart.');
      return; // Exit the function if data is missing
    }

    const canvas = document.getElementById('CMCPChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Assuming both datasets have the same labels
    const labels = activeLicensesData.map(
      (dataItem: { label: any }) => dataItem.label
    );

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Active Licenses',
            data: activeLicensesData.map(
              (dataItem: { value: any }) => dataItem.value
            ),
            backgroundColor: 'rgba(29, 78, 216, 0.2)',
            borderColor: 'rgba(29, 78, 216, 1)',
            borderWidth: 2,
          },
          {
            label: 'Total Licenses',
            data: totalLicensesData.map(
              (dataItem: { value: any }) => dataItem.value
            ),
            backgroundColor: 'rgba(255, 99, 132, 0.4)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
              },
            },
          ],
        },
      },
    });
  }

  //test data - leave
  // createChart() {
  //   const canvas = document.getElementById('EBCPCMChart') as HTMLCanvasElement;
  //   if (!canvas) return;
  //   const ctx = canvas.getContext('2d');
  //   if (!ctx) return;
  //   // Months of the year for the labels
  //   const labels = [
  //     'January',
  //     'February',
  //     'March',
  //     'April',
  //     'May',
  //     'June',
  //     'July',
  //     'August',
  //     'September',
  //     'October',
  //     'November',
  //     'December',
  //   ];
  //   // Dummy data for Active and Total Licenses
  //   const activeLicensesData = [
  //     120, 150, 180, 200, 210, 200, 230, 250, 240, 260, 270, 280,
  //   ];
  //   const totalLicensesData = [
  //     200, 250, 300, 350, 400, 420, 430, 450, 470, 480, 490, 500,
  //   ];
  //   this.chart = new Chart(ctx, {
  //     type: 'line',
  //     data: {
  //       labels: labels,
  //       datasets: [
  //         {
  //           label: 'Active Licenses',
  //           data: activeLicensesData,
  //           backgroundColor: 'rgba(29, 78, 216, 0.2)',
  //           borderColor: 'rgba(29, 78, 216, 1)',
  //           borderWidth: 2,
  //         },
  //         {
  //           label: 'Total Licenses',
  //           data: totalLicensesData,
  //           backgroundColor: 'rgba(255, 99, 132, 0.2)',
  //           borderColor: 'rgba(255, 99, 132, 1)',
  //           borderWidth: 2,
  //         },
  //       ],
  //     },
  //     options: {
  //       responsive: true,
  //       maintainAspectRatio: false,
  //       scales: {
  //         yAxes: [
  //           {
  //             ticks: {
  //               beginAtZero: true,
  //             },
  //           },
  //         ],
  //       },
  //     },
  //   });
  // }
}
